import BaseModal from 'components/BaseModal'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { number } from 'prop-types'
import { MODAL_SIZE_EXTRA_LARGE } from 'constants/Layout';
import { LoadingIcon } from 'components/Icons';
import { Collapse, Divider, Spin, Typography } from 'antd';
import { CellRenderers } from 'utils/tableUtils';
import { getUrlSearch } from "utils/urlUtil";
import _, { isNil } from 'lodash';
import Vocabulary from 'components/Vocabulary';
import { PermissionSetMap } from 'constants/RBACConstants';
import ModalUtil from 'utils/modalUtil';
import ConfirmPopup from 'components/ConfirmPopup';
import { FINDING_ACTIONS } from './Constants';
import classNames from 'classnames';
import ContentOverlay from 'components/ContentOverlay';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { isCustomFinding, similarFindingsCount } from './utils';
import { CVE } from './CVE';
import { ScoreBox, RenderExploitedIcon, RenderTrendingIcon } from 'views/Feeds/FeedDetailsModal';
import FeedsAPI from 'views/Feeds/FeedsAPI';
import { ThreatTag } from 'components/ThreatsTag';
import { useGlobalSettingsContext } from 'contexts/GlobalSettingsContext';
import  { RiskScoreDisableTag } from 'utils/riskScoreUtil';
import FindingDetailsProvider, { useFindingDetails } from './FindingDetails/FindingDetailsProvider';
import FindingProposalDetails from './FindingDetails/FindingProposalDetails';
import FindingDetailsTitle from './FindingDetails/FindingDetailsTitle';
import { FindingDetailsGrid } from './FindingDetails/FindingDetailsGrid';
import { FindingDetailsDescription } from './FindingDetails/FindingDetailsDescription';
import { FindingDetailsSourceInfo } from './FindingDetails/FindingDetailsSourceInfo';
import { FindingDetailsComments } from './FindingDetails/FindingDetailsComments';
import {
    SeverityField,
    CategoryField,
    FindingScoreField,
    StatusField,
    EngagementField,
    ComplianceField,
    AMCRuleField,
    CVSSVectorField,
    ImpactScoreField,
    ExploitabilityScoreField,
    CVEField,
    CWEField,
    DueOnField,
    LastChangedField,
    LastSeenField,
    LastMitigatedField,
    LastTriagedField,
    LastAcceptRiskField,
    LastConfirmedField,
    LastFalsePositiveField,
    LastOpenedField,
    LastSuppressedField,
    OwnerField,
    SecurityOwnerField,
    TeamField,
    AATIScoreField
} from './FindingDetails/PinnableFields/FieldDefinitions';
import FindingDetailsAction from './FindingDetails/FindingDetailsAction';
import { FindingDetailsTag } from './FindingDetails/FindingDetailsTag';
import { FindingDetailsTickets } from './FindingDetails/FindingDetailsTickets';
import FindingRiskScorePopover from 'components/FindingRiskScorePopover';
import ACPageContainer from 'ac-components/ACPageContainer/ACPageContainer';
import ACRiskScoreTag from 'ac-components/ac-riskscore-tag/ACRiskScoreTag';

const { Link } = Typography;

const FindingDetailsModalV2 = forwardRef(function FindingDetailsModalV2({ findingId: id, afterClose, bucket, history }, ref) {
    const thisRef = useRef();
    const findingTagsRef = useRef();

    useImperativeHandle(ref, () => ({ ...thisRef.current }), []);

    const onCloseClick = useCallback((closeModal) => {
        const showWarningPopUp = findingTagsRef.current?.checkUnSaveChanges()??false;
        if (showWarningPopUp) {
            ModalUtil.show({
                content: (
                    <ConfirmPopup 
                        promptMessage={"You have unsave changes, still Do you want to proceed?"}
                        okText={"Yes"}
                        onOk={closeModal}
                        modalProps={{
                            cancelBtnText: "No"
                        }}
                    />
                )
            })

        }
        return showWarningPopUp;
    }, []);

    return (
        <FindingDetailsProvider bucket={bucket} id={id} history={history}>
            <BaseModal 
                ref={thisRef}
                afterClose={afterClose} 
                modalProps={{
                    width: MODAL_SIZE_EXTRA_LARGE, 
                    className: "modal-v2 modal-full-height no-footer",
                    title: <FindingTitle />,
                    footer: null,
                    onCancel: onCloseClick
                }}
            >
                <FindingBasicInfo  />
                <div className='m-t-sm' />
                <FindingProposals designV3={false} />
                <FindingDetailsTickets designV3={false} />
                <div className='m-t-sm' />
                <FindingVulnerabilityInfo />
                <div className='m-t-sm' />
                <FindingDetailsSourceInfo designV3={false} />
                <div className='m-t-sm' />
                <FindingDetailsDescription designV3={false} />
                <div className='m-t-sm' />
                <FindingDetailsTag designV3={false} />
                <div className='m-t-sm' />
                <FindingDetailsComments designV3={false} />
            </BaseModal>
        </FindingDetailsProvider>
    )
});

FindingDetailsModalV2.propTypes = {
    findingId: number.isRequired
}

export function FindingProposals(designV3 = true){
    const {loading, finding} = useFindingDetails();
    if(loading || !finding?.underReview){
        return null;
    }
    return (
        <Spin spinning={loading}>
            <ACPageContainer.Section>
                <FindingProposalDetails />
            </ACPageContainer.Section>
        </Spin>
    );
}

function FindingTitle() {
    return (
        <div className='d-flex justify-content-between'>
            <FindingDetailsTitle />
            <div>
                <FindingDetailsAction />
            </div>
        </div>
    )
}

function FindingBasicInfo() {
    const [visibleFieldsLength, setVisibleFieldsLength] = useState(10);
    const {
        history,
        showFindingActionPopup,
        finding,
        loading,
        openFindingFieldEditPopup,
        showDueDatePopup,
        handleArmorcodeCategoryChange,
        handleArmorcodeSeverityChange,
    } = useFindingDetails();
    const globalSettings = useGlobalSettingsContext();
    const _isCustomFinding = isCustomFinding(finding?.testType);
    const featureFlags = useFeatureFlag();
    
    const list = useMemo(() => [
        { label: "", path: "title", span: 24, formatter:  (title) =><ContentOverlay className='markdown-text m-b-sm' contentClass='d-flex' content={CellRenderers.VALUE_OR_NA(title)} />, editAction: _isCustomFinding ?  () => openFindingFieldEditPopup("title") : undefined , actionPermission: PermissionSetMap.UPDATE_FINDINGS},
        { 
            ...SeverityField,
            formatter: (severity, finding) => SeverityField.formatter(severity, finding, handleArmorcodeSeverityChange),
            span: 8
        },
        { 
            ...CategoryField,
            formatter: (category) => CategoryField.formatter(category, handleArmorcodeCategoryChange),
            span: 8
        },
        { ...OwnerField, editAction: () => showFindingActionPopup(FINDING_ACTIONS.CHANGE_OWNER), span: 8 },
        {...StatusField, span: 8 },
        { label: "Found On", path: "foundOn", formatter: foundOn => foundOn ? <>{CellRenderers.DATE(foundOn)} ({CellRenderers.AGE(foundOn)})</> : CellRenderers.VALUE_OR_NA()},
        { label: <><Vocabulary name="resolution"/> Due Date</>, ...DueOnField, editAction: showDueDatePopup, span: 8 },
        ...(featureFlags.RISK_SCORE_3_0 ? [
            {
              label: <><Vocabulary name="riskScore"/></>,
              path: "riskScore",
              formatter: (riskScore, finding) =>
                globalSettings.riskScoreEnabled ? (
                  <FindingRiskScorePopover finding={finding}>
                    <ACRiskScoreTag value={riskScore} />
                  </FindingRiskScorePopover>
                ) : (
                  <RiskScoreDisableTag history={history} />
                ),
            },
          ]
        : []), 
        { ...FindingScoreField, span: 8 },
        ...(featureFlags.RISK_SCORE_3_0 ? [{ label: <Vocabulary name="assetScore" />, path: "assetScore", span: 8 }] : []),
        { ...TeamField, span: 8},
        { ...SecurityOwnerField, span: 8 },
        { ...EngagementField, span: 8 },
        { ...ComplianceField, span: 8 },
        {
            ...AMCRuleField,
            editAction: _isCustomFinding ? () => openFindingFieldEditPopup("ruleKey") : undefined,
            span: 8
        },
        { ...LastSeenField, span: 8 },
        { ...LastChangedField, span: 8 },
        { ...LastMitigatedField, span: 8 },
        { label: "Correlated findings", path: "similarFindings", formatter: (similarFindings)=>   similarFindingsCount(similarFindings, history) },
        { ...LastTriagedField, label: <>Last <Vocabulary name={"triage"} /> Date</>, span: 8 },
        { ...LastConfirmedField, label: <>Last <Vocabulary name={"confirmed"} /> Date</>, span: 8 },
        { ...LastFalsePositiveField, label: <>Last <Vocabulary name={"falsePositive"} /> Date</>, span: 8 },
        { ...LastOpenedField, label: <>Last <Vocabulary name={"open"} /> Date</>, span: 8 },
        { ...LastAcceptRiskField, label: <>Last <Vocabulary name={"acceptRisk"} /> Date</>, span: 8 },
        { ...LastSuppressedField, label: <>Last <Vocabulary name={"suppressed"} /> Date</>, span: 8 },
    ], [showFindingActionPopup, handleArmorcodeCategoryChange, handleArmorcodeSeverityChange, showDueDatePopup, openFindingFieldEditPopup, _isCustomFinding, history, featureFlags.RISK_SCORE_3_0, globalSettings.riskScoreEnabled]);

    const toggleFields = useCallback(() => {
        setVisibleFieldsLength(prev => prev === list.length ? 10 : list.length);
    }, [list]);

    return (
        <Spin spinning={loading}>
            <FindingDetailsGrid list={list.slice(0, visibleFieldsLength)} data={finding} labelWidth={85} />
            <div className='text-right'>
                <Link onClick={toggleFields}>...Show {visibleFieldsLength !== list.length ? "more" : "less"}</Link>
            </div>
        </Spin>
    )
}

function FindingVulnerabilityInfo() {
    const {
        finding,
        loading,
        openFindingFieldEditPopup,
        showCVSSVectorComponents,
        cveDetails: _cveDetails,
    } = useFindingDetails();
    const _isCustomFinding = isCustomFinding(finding?.testType);
    const featureFlags = useFeatureFlag();
    const aatiScoreFeatureFlag = featureFlags.AATI_SCORE_COLUMN;
    const TITLE = 'VULNERABILITY INFORMATION';
    const list = useMemo(() => {
        return [
            { 
                ...CVEField, span: 10,
                editAction: _isCustomFinding ? () => openFindingFieldEditPopup("cves") : undefined
            },
            { 
                ...CVSSVectorField, span: 14, 
                editAction:  _isCustomFinding ? () => openFindingFieldEditPopup("cvssVector") : undefined ,
                formatter: (vector, finding) => {
                    const cvssVector = _.get(finding, 'additionalDetails.cvssVectorV2', '');
                    const isDisabled = cvssVector ? ( cvssVector.startsWith("CVSS")? false : true) : false;
                    return isDisabled ? CellRenderers.VALUE_OR_NA(vector) : CVSSVectorField.formatter(vector, finding, showCVSSVectorComponents)
                },
                editButtonToolTip:"Cannot edit CVSS 2.0 version vector",
                disableEdit: (data, label) => { 
                    const cvssVector = _.get(data, 'additionalDetails.cvssVectorV2', '');
                    return label === 'CVSS Vector' && cvssVector ? ( cvssVector.startsWith("CVSS")? false : true) : false;
                }
            },
            { 
                label: "Base Score", path: "baseScore", span: 10, 
                editAction:  _isCustomFinding ? () => openFindingFieldEditPopup("cvssScore") : undefined , actionPermission: PermissionSetMap.UPDATE_FINDINGS 
            },
            { ...ImpactScoreField, span: 14 },
            { ...ExploitabilityScoreField, span: 10 },
            {
                ...CWEField, span: 10,
                editAction: _isCustomFinding ? () => openFindingFieldEditPopup("cwes") : undefined 
            },
            { 
                label: "Exploit Available",
                path: "additionalDetails.exploited",
                span: 10,
                formatter: (value) => _.isUndefined(value) ? CellRenderers.VALUE_OR_NA() : _.capitalize(value),
                actionPermission: PermissionSetMap.UPDATE_FINDINGS,
                editAction: _isCustomFinding ? () => openFindingFieldEditPopup("exploited") : undefined,
            }, { 
                label: "CTI SCORE",
                path: "additionalDetails.ctiScore",
                span: 10,
                formatter: (value) => CellRenderers.VALUE_OR_NA(value),
            }, { 
                label: "THREAT SOURCE",
                path: "additionalDetails.threatSource",
                span: 10,
                formatter: (value) => CellRenderers.VALUE_OR_NA(value),
            }, { 
                label: "THREAT SOURCE URL",
                path: "additionalDetails.threatSourceUrl",
                span: 10,
                formatter: (value) => isNil(value) ? CellRenderers.VALUE_OR_NA(value) : <Typography.Link href={value} target='_blank'>{value}</Typography.Link>,
            }
        ]
    }, [openFindingFieldEditPopup, _isCustomFinding, showCVSSVectorComponents])

    const AATIFieldList = useMemo(() => {
        return [
            { ...AATIScoreField,
                label: "ArmorCode Advanced Threat Intelligence (AATI) Score",
                featureFlag: aatiScoreFeatureFlag,
              formatter: (score) => AATIScoreField.formatter(score, finding),
            }
        ];
    }, [aatiScoreFeatureFlag, finding]);

    const ThreatTagsList = ()=> {
        return (_cveDetails ? <div className="d-flex align-items-center m-b-sm m-t-xs">
        {_.get(_cveDetails.threatTags, "proof of concept") && <ThreatTag title={"Proof Of Concept"} count={_.get(_cveDetails.threatTags, "proof of concept")} className={"m-r-sm"}/>}
        {_.get(_cveDetails.threatTags, "in the wild") && <ThreatTag title={"In The Wild"} count={_.get(_cveDetails.threatTags, "in the wild")} className={"m-r-sm"}/>}
        {_.get(_cveDetails.threatTags, "pentester framework") && <ThreatTag title={"Pentester Framework"} count={_.get(_cveDetails.threatTags, "pentester framework")} className={"m-r-sm"}/>}
        {_.get(_cveDetails.threatTags, "code snippet") && <ThreatTag title={"Code Snippet"} count={_.get(_cveDetails.threatTags, "code snippet") ? "Y" : "N"} className={"m-r-sm"}/>}
        {!_.isNil(_cveDetails.epssScore) && <span style={{fontWeight: 800}}>EPSS: {_cveDetails.epssScore}</span>}
      </div> : null)
    }
    
    const header = (
        <div className='d-flex align-items-center justify-content-between'>
            <div>{TITLE}</div>
            {aatiScoreFeatureFlag && <VulnerabilityExtras />}
        </div>
    );

    return (
        <Collapse className='collapse-v2'>
            <Collapse.Panel header={header} key="vulnerability">
                <Spin spinning={loading}>
                    <FindingDetailsGrid list={AATIFieldList} data={finding} labelWidth={366} />
                    {aatiScoreFeatureFlag && <ThreatTagsList/>}
                    <FindingDetailsGrid list={list} data={finding} />
                </Spin>
            </Collapse.Panel>
        </Collapse>
    )
}

function VulnerabilityExtras() {
    const { finding, loading } = useFindingDetails();
    const [data, setData] = useState();
    const CVEId = _.get(finding, "additionalDetails.cveUsed");
    const exploited = _.get(finding, "additionalDetails.exploited");
    const isTrending = _.get(data, 'otherProperties.isTrending');
    

    useEffect(() => {
        if(loading) {
            return;
        }
        if (!CVEId) {
            setData([]);
            return;
        }
        setData();
        const { promise, cancel } = FeedsAPI.getFeedDetails('CSW', CVEId);
        promise.then(setData);
        return cancel;
    }, [CVEId, loading]);

    const cves = useMemo(() => {
        if (!finding) {
            return [];
        }
        return [CVEId, ..._.filter(finding.cves, (cve) => cve !== CVEId)];

    }, [finding, CVEId]);

    const urlParams = useMemo(() => getUrlSearch({filters: { feedId: CVEId }}), [CVEId]);

    return (
        <div className='d-flex align-items-center' onClick={(e) => e.stopPropagation()}>
            {
                _.isUndefined(data) ?
                    <LoadingIcon />
                    :
                    !_.isEmpty(data) ?
                        <>
                            {CVE(cves)}
                            {!_.isNil(data.otherProperties) && <Link target='_blank' href={`#/feeds?${urlParams}`} className='d-flex align-items-center m-l-xs' style={{ background: '#fff', padding: '2px 6px', textDecoration: 'none' }}>
                                <ScoreBox tooltip='AATI Score' score={_.get(data, 'otherProperties.definitiveScore')} />
                                <ScoreBox tooltip='Base Score' score={_.get(data, 'otherProperties.baseScore')} border />
                                {isTrending && <RenderTrendingIcon />}
                                {exploited && <RenderExploitedIcon className={classNames({ 'm-l-xs': isTrending })} />}
                            </Link>}
                        </>
                        :
                        null
            }
            
        </div>
    );
}

export const CollapseHeaderDivider = () => <Divider type='vertical' className='m-x-m' style={{ height: "1.5em" }} />;

export default FindingDetailsModalV2;
